import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AuditOutlined, CloseCircleOutlined, CopyOutlined, CreditCardOutlined, MoreOutlined, SwapOutlined } from "@ant-design/icons";
import { App, Button, Dropdown, theme } from "antd";
import { observer } from "mobx-react";
import { useStores } from "../../../../../store/stores.js";
import { __ } from "../../../../../utils/i18n.js";
const SubscriptionActionsOverflow = observer(({ subscription })=>{
    const { accountV2Store } = useStores();
    const { message } = App.useApp();
    const { token } = theme.useToken();
    const { data: { paymentInformation } } = subscription;
    // One-time plans (incl. active "lifetime") share a different action set:
    // there is no recurring billing to update and nothing to cancel, so we
    // strip those items entirely. Upgrade stays visible but disabled with a
    // tooltip explaining why.
    const upgradeDisabled = !subscription.canBeModified || !subscription.isActive || subscription.isPastDue || subscription.isOneTime;
    const changePaymentDisabled = !subscription.canBeModified || !subscription.isActive || !paymentInformation;
    // When the contextual CTA already surfaces "Update payment method" on a
    // past-due subscription, hide the menu item to avoid a duplicate path.
    const hideChangePayment = subscription.isOneTime || subscription.isPastDue;
    const hideCancel = subscription.isOneTime;
    const { key: contractKey, id: contractId } = subscription.contractOrCustomerOrderNumberLabel;
    const copyContractLabel = contractKey === "Contract" ? __("Copy contract number") : __("Copy order number");
    const copyContractToastLabel = contractKey === "Contract" ? __("Contract number") : __("Order number");
    const handleCopyContract = async ()=>{
        try {
            await navigator.clipboard.writeText(contractId);
            message.info(__("%s copied to clipboard.", copyContractToastLabel));
        } catch  {
            // Clipboard API can fail (insecure context, denied permission);
            // surface the ID inline so the customer can still copy it by hand.
            const fallback = contractKey === "Contract" ? __("Couldn't copy to clipboard. Contract number: %s", contractId) : __("Couldn't copy to clipboard. Order number: %s", contractId);
            message.warning({
                content: fallback,
                duration: 8
            });
        }
    };
    // The upgrade item stays visible on one-time plans so the affordance is
    // discoverable; a hover tooltip explains why it isn't actionable. The
    // tooltip is *only* attached when one-time / lifetime is the actual
    // disable reason — past-due or cancelled subscriptions get the plain
    // disabled item so the wording stays accurate.
    // The upgrade row is the positive "happy path": when actionable, both
    // the icon and the label are tinted in the brand colour so the option
    // visually leads the menu. When disabled it falls back to AntD's muted
    // state so it doesn't compete with the rest of the rows.
    //
    // For one-time / lifetime plans we render the explanation as a permanent
    // second line *inside* the menu item instead of a hover tooltip. The
    // tooltip approach kept getting clipped behind the dropdown overlay's
    // stacking context (no combination of `getPopupContainer` + `zIndex`
    // forced it above the menu items reliably), and a permanent in-row
    // explanation is also better for keyboard / screen-reader users who
    // never trigger hover. Width is clamped so the row doesn't push past
    // the dropdown's natural width.
    const upgradeText = __("Upgrade or downgrade");
    const upgradeLabelStyle = upgradeDisabled ? undefined : {
        color: token.colorPrimary,
        fontWeight: 500
    };
    const upgradeBlockedByOneTime = subscription.isOneTime && subscription.isActive && !subscription.isPastDue;
    const upgradeBlockedByCancellation = subscription.isCancellationPending || subscription.isCancelled;
    const renderExplainedLabel = (explanation)=>/*#__PURE__*/ _jsxs("div", {
            style: {
                maxWidth: 260,
                whiteSpace: "normal",
                lineHeight: 1.4,
                paddingTop: 4,
                paddingBottom: 4
            },
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: upgradeText
                }),
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        fontSize: 11,
                        color: token.colorTextTertiary,
                        marginTop: 2
                    },
                    children: explanation
                })
            ]
        });
    let upgradeLabel = /*#__PURE__*/ _jsx("span", {
        style: upgradeLabelStyle,
        children: upgradeText
    });
    if (upgradeBlockedByOneTime) {
        upgradeLabel = renderExplainedLabel(__("Your lifetime purchase can't be upgraded or downgraded. If you need more licenses, you can buy an additional license package any time."));
    } else if (upgradeBlockedByCancellation) {
        upgradeLabel = renderExplainedLabel(__("Plan changes aren't possible once a subscription has been cancelled."));
    }
    const upgradeIcon = /*#__PURE__*/ _jsx(SwapOutlined, {
        style: upgradeDisabled ? undefined : {
            color: token.colorPrimary
        }
    });
    const items = [
        {
            key: "upgrade",
            icon: upgradeIcon,
            disabled: upgradeDisabled,
            label: upgradeLabel,
            onClick: ()=>accountV2Store.openChangePlanFor(subscription)
        },
        !hideChangePayment && {
            key: "change-payment",
            icon: /*#__PURE__*/ _jsx(CreditCardOutlined, {}),
            disabled: changePaymentDisabled,
            label: __("Change payment details"),
            onClick: ()=>accountV2Store.openChangePaymentFor(subscription)
        },
        {
            key: "change-invoice-address",
            icon: /*#__PURE__*/ _jsx(AuditOutlined, {}),
            disabled: subscription.isCancelled || subscription.isPastDue,
            label: __("Change invoice address"),
            onClick: ()=>accountV2Store.openChangeInvoiceAddressFor(subscription)
        },
        {
            key: "copy-contract",
            icon: /*#__PURE__*/ _jsx(CopyOutlined, {}),
            label: copyContractLabel,
            onClick: handleCopyContract
        },
        !hideCancel && subscription.isActive && {
            key: "cancel",
            icon: /*#__PURE__*/ _jsx(CloseCircleOutlined, {}),
            disabled: subscription.isPastDue,
            label: subscription.isPastDue ? /*#__PURE__*/ _jsxs("div", {
                style: {
                    maxWidth: 260,
                    whiteSpace: "normal",
                    lineHeight: 1.4,
                    paddingTop: 4,
                    paddingBottom: 4
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: __("Cancel subscription")
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            fontSize: 11,
                            color: token.colorTextTertiary,
                            marginTop: 2
                        },
                        children: __("The subscription has already renewed. Settle the open invoice before you can cancel it.")
                    })
                ]
            }) : __("Cancel subscription"),
            onClick: ()=>accountV2Store.openCancellationFor(subscription)
        }
    ].filter(Boolean);
    return /*#__PURE__*/ _jsx(Dropdown, {
        menu: {
            items
        },
        placement: "bottomRight",
        trigger: [
            "click"
        ],
        children: /*#__PURE__*/ _jsx(Button, {
            type: "text",
            icon: /*#__PURE__*/ _jsx(MoreOutlined, {}),
            "aria-label": __("Subscription actions")
        })
    });
});
export { SubscriptionActionsOverflow };
